import React, { useEffect, useMemo, useState } from 'react';
import { format, getDay, getDaysInMonth } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import Holidays from 'date-holidays';
import { useGetAssetsAvailabilityDataQuery } from '../../api/booking/bookingApiSlice';
import { useGetHousesQuery } from '../../api/inventory/inventoryApiSlice';
import { useGetAdminGroupsQuery, useGetProductsQuery } from '../../api/group/groupAdminApiSlice';
import { useTenantProductCategoriesQuery } from '../../hooks/useTenantProductCategoriesQuery';
import { useGetHolidaysInRangeQuery } from '../../api/account/accountApiSlice';
import { stateHolidayNames } from './availability/stateHolidays';
import { serviceNamesByCellId } from './availability/serviceNames';
import type { HouseListItem } from '../../api/station/stationApiTypes';
import { ROOM_PRODUCT_USE_CASES } from '../services/serviceConfigurationRules';
import { hasLockerUseCase } from '../../common/lockerUseCases';
import { BookingStatusType } from '../../components/StatusBadge';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../../store/store';
import {
    Button, CircularProgress,
    Dialog, DialogActions, DialogContent, DialogTitle,
    Tooltip, alpha,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { AssetAvailability, OrderForAssetAvailability } from '../../api/types';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { makeAssetDetailsUrl, makeStationDetailsUrl } from '../../routes';
import { palette } from '../../material-ui/palette';
import { enqueueErrorSnackbar } from '../../action/snackbarCommands';
import {
    assetDisplayLabel,
    getDominantStatus,
    occupiesAsset,
    statusColors,
} from './availability/availabilityStatus';
import AssetDayOrders, { DayTimelineHead } from './availability/AssetDayOrders';
import DayOccupancyOverlay, { DayOverlayObject } from './availability/DayOccupancyOverlay';
import {
    ProductRow,
    StationRow,
    groupByStation,
    stationHasBookingOn,
} from './availability/assetGrouping';
import { emptyHousesToShow, houseNamesById } from './availability/houseRows';
import { stationCalendarDay, stationDayEnd, stationInstant } from '../../utils/stationTime';

const PREFIX = 'AssetAvailabilityChart';

const classes = {
    dialogTitle: `${PREFIX}-dialogTitle`,
    dialogContent: `${PREFIX}-dialogContent`,
    closeButton: `${PREFIX}-closeButton`,
    backdropOpacity: `${PREFIX}-backdropOpacity`,
};

const StyledDialog = styled(Dialog)(() => ({
    [`& .${classes.dialogTitle}`]: {
        textTransform: 'none',
        fontSize: '16px',
        lineHeight: '20px',
        fontWeight: '600',
        color: palette.text.primary,
    },
    [`& .${classes.dialogContent}`]: {
        textTransform: 'none',
        fontSize: '16px',
        lineHeight: '20px',
        fontWeight: '500',
        color: palette.text.primary,
    },
    [`& .${classes.closeButton}`]: {
        height: '44px',
        padding: '12px',
        textTransform: 'none',
        fontSize: '16px',
        fontStyle: 'normal',
        lineHeight: '20px',
        letterSpacing: '0.32px',
        fontWeight: '600',
        borderRadius: '12px',
    },
    [`& .${classes.backdropOpacity}`]: {
        backgroundColor: alpha(palette.primary.main, 0.8),
    },
}));

/** What the overview is currently showing, for the page title's subtitle. */
export interface AssetOverviewSummary {
    stations: number;
    objects: number;
}

interface AssetAvailabilityChartProps {
    searchQuery?: string;
    allowedUseCases?: string[];
    /**
     * How much the chart is currently showing, for the page title's subtitle.
     *
     * Reported upwards rather than recomputed above: the counts fall out of the
     * grouping, which needs the month and the filters — and both live here. The
     * page owns the header, so it gets told rather than asked.
     */
    onSummaryChange?: (summary: AssetOverviewSummary) => void;
}

export const AssetAvailabilityChart: React.FC<AssetAvailabilityChartProps> = (
    { searchQuery = '', allowedUseCases, onSummaryChange },
) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    // The month grid is station calendar days (ADR-0019): "today" and the day
    // a booking falls on are read on the station's clock, not the browser's.
    const todayLabel = stationCalendarDay(new Date());
    const [selectedMonth, setSelectedMonth] = useState<number>(Number(todayLabel.slice(5, 7)) - 1);
    const [selectedYear, setSelectedYear] = useState<number>(Number(todayLabel.slice(0, 4)));
    const dayLabel = (day: number): string =>
        `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const [selectedStatus, setSelectedStatus] = useState<BookingStatusType | 'all'>('all');
    const [selectedLocation, setSelectedLocation] = useState<string>('all');
    const [selectedUseCase, setSelectedUseCase] = useState<string>('all');

    const config = useSelector(
        (state: RootState) => state.authentication.config,
    );
    const selectedTenant = useSelector(
        (state: RootState) => state.authentication.tenant,
    );

    const hasLockerServices = hasLockerUseCase(config?.services);
    // Houses hold rooms, so only room tenants have any (houseRows.ts).
    const hasRoomServices = Boolean(config?.services?.some(
        (service: string) => ROOM_PRODUCT_USE_CASES.has(service),
    ));
    const { data: houses = [] } = useGetHousesQuery(undefined, { skip: !hasRoomServices });
    // The service column names the product's category (#3167). Secondary:
    // while these load, or if one fails, rows show the use case as before.
    const { data: cells = [] } = useGetAdminGroupsQuery();
    const { data: products = [] } = useGetProductsQuery();
    const { data: categories = [] } = useTenantProductCategoriesQuery();
    const serviceNames = useMemo(
        () => serviceNamesByCellId(cells, products, categories),
        [cells, products, categories],
    );

    const holidayCountry = config?.country ?? 'DE';
    const holidays = useMemo(() => new Holidays(holidayCountry), [holidayCountry]);
    // A room tenant with a holiday region gets that state's public holidays
    // (M9, #3041); everyone else keeps the nationwide ones above.
    const monthRange = {
        from: `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-01`,
        to: `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(getDaysInMonth(new Date(selectedYear, selectedMonth))).padStart(2, '0')}`,
    };
    const { data: holidayRange, isFetching: holidaysLoading } = useGetHolidaysInRangeQuery(
        monthRange,
        { skip: !hasRoomServices },
    );
    const stateHolidays = useMemo(
        () => stateHolidayNames(holidayRange, i18n.language),
        [holidayRange, i18n.language],
    );

    const {
        data: assetsData,
        isLoading,
        isFetching,
        error,
        refetch,
    } = useGetAssetsAvailabilityDataQuery({
        data: {
            year: selectedYear.toString(),
            month: (selectedMonth + 1).toString().padStart(2, '0'),
        },
    });

    // Tenant change requires manual refetch (tenant is in auth headers, not query args)
    useEffect(() => {
        if (selectedTenant) {
            refetch();
        }
    }, [selectedTenant, refetch]);

    useEffect(() => {
        if (error) {
            dispatch(enqueueErrorSnackbar({
                message: t('dashboard.assets_availability.error'),
            }));
        }
    }, [error, dispatch, t]);

    const daysInMonth = getDaysInMonth(new Date(selectedYear, selectedMonth));
    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

    const dayMeta = useMemo(() => days.map((d) => {
        const date = new Date(selectedYear, selectedMonth, d);
        const dayOfWeek = getDay(date);
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
        const isToday = dayLabel(d) === todayLabel;
        const stateHoliday = stateHolidays?.get(dayLabel(d));
        const holidayResult = stateHolidays || holidaysLoading ? false : holidays.isHoliday(date);
        const nationwide = Array.isArray(holidayResult)
            ? holidayResult.find((h) => h.type === 'public')
            : null;
        const holidayName = stateHoliday ?? nationwide?.name ?? '';
        const weekdayShort = format(date, 'EEEEEE', {
            locale: i18n.language === 'de' ? de : enUS,
        });
        return {
            day: d,
            isWeekend,
            isToday,
            isHoliday: holidayName !== '',
            holidayName,
            weekdayShort,
        };
    }), [selectedYear, selectedMonth, i18n.language, todayLabel, stateHolidays, holidaysLoading, holidays]);

    const locationOptions = useMemo(() => {
        if (!assetsData || !hasLockerServices) return [];
        return Array.from(
            new Set(assetsData.map((asset) => asset.locationName)),
        );
    }, [assetsData, hasLockerServices]);

    const filteredData = useMemo(() => {
        if (!assetsData) return [];

        let filtered: AssetAvailability[] = assetsData;

        if (allowedUseCases?.length) {
            filtered = filtered.filter(
                (asset) => asset.useCases?.some((uc) => allowedUseCases.includes(uc)),
            );
        }

        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter((asset) =>
                asset.assetName.toLowerCase().includes(query) ||
                asset.locationName?.toLowerCase().includes(query),
            );
        }

        if (hasLockerServices && selectedLocation !== 'all') {
            filtered = filtered.filter(
                (asset) => asset.locationName === selectedLocation,
            );
        }

        if (selectedStatus !== 'all') {
            filtered = filtered.map((asset) => ({
                ...asset,
                orders: asset.orders.filter(
                    (order) => order.status === selectedStatus,
                ),
            }));
        }

        if (selectedUseCase !== 'all') {
            filtered = filtered.filter(
                (asset) => asset.useCases?.includes(selectedUseCase),
            );
        }

        return filtered;
    }, [assetsData, selectedStatus, selectedLocation, selectedUseCase, searchQuery, hasLockerServices, allowedUseCases]);

    const sortAssets = (a: AssetAvailability, b: AssetAvailability) => {
        if (!hasLockerServices) {
            return a.assetName.localeCompare(
                b.assetName, undefined,
                { numeric: true, sensitivity: 'base' },
            );
        }
        const aDoorRaw = Array.isArray(a.doorNumbers)
            ? a.doorNumbers[0] : undefined;
        const bDoorRaw = Array.isArray(b.doorNumbers)
            ? b.doorNumbers[0] : undefined;
        const aDoor = aDoorRaw !== undefined
            ? parseInt(String(aDoorRaw).replace(/[^0-9]/g, ''), 10)
            : Number.MAX_SAFE_INTEGER;
        const bDoor = bDoorRaw !== undefined
            ? parseInt(String(bDoorRaw).replace(/[^0-9]/g, ''), 10)
            : Number.MAX_SAFE_INTEGER;
        if (!isNaN(aDoor) && !isNaN(bDoor) && aDoor !== bDoor) {
            return aDoor - bDoor;
        }
        return a.assetName.localeCompare(
            b.assetName, undefined,
            { numeric: true, sensitivity: 'base' },
        );
    };

    // One ordering for the table and for the day overlay: the overlay has to
    // read as the same list of objects, top to bottom.
    const sortedAssets = useMemo(
        () => filteredData.slice().sort(sortAssets),
        [filteredData, hasLockerServices],
    );

    // Station → product → objects. A locker station used to render one row per
    // compartment, all with the same location and service; at a hundred
    // stations the page is unusable. The rules — why the product level folds
    // away for a single-unit product, why an object without a station becomes
    // one — live in `assetGrouping`, where they are tested.
    const stationRows = useMemo(() => groupByStation(sortedAssets), [sortedAssets]);
    const houseNames = useMemo(() => houseNamesById(houses), [houses]);
    // A tenant with exactly one house sees it open: its rooms are the page,
    // and a closed row would have to be opened on every visit (PO,
    // 2026-10-08). Only when that house is an expandable row here.
    const autoOpenStationId = useMemo(() => {
        if (houses.length !== 1) return null;
        const only = houses[0] as HouseListItem;
        return stationRows.some((row) => row.stationId === only.id && row.objectCount > 1)
            ? only.id : null;
    }, [houses, stationRows]);
    const emptyHouses = useMemo(
        () => emptyHousesToShow(houses, { searchQuery, selectedUseCase, allowedUseCases }),
        [houses, searchQuery, selectedUseCase, allowedUseCases],
    );

    useEffect(() => {
        // Nothing is reported while loading: the grouping of an empty list is a
        // real-looking "0 stations", and a provisional zero claims there are
        // none (design-decisions.md, counts). On a month switch the previous
        // number stays until the new one is known, which is the lesser lie.
        if (isLoading) return;

        onSummaryChange?.({
            stations: stationRows.length,
            objects: stationRows.reduce((total, station) => total + station.objectCount, 0),
        });
    }, [stationRows, onSummaryChange, isLoading]);

    // Stations whose expansion the user flipped. Flipped against the default,
    // which is closed — except for a tenant's only house, which opens by
    // itself (`autoOpenStationId`), so its rooms are what the page shows.
    const [expandedStations, setExpandedStations] = useState<Set<string>>(new Set());
    const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());

    const toggleKey = (
        setter: React.Dispatch<React.SetStateAction<Set<string>>>,
        key: string,
    ) => setter((current) => {
        const next = new Set(current);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    // A filter change replaces the rows, so an expansion held over from the
    // previous set would point at a station no longer on screen.
    useEffect(() => {
        setExpandedStations(new Set());
        setExpandedProducts(new Set());
    }, [
        selectedLocation, selectedUseCase, selectedStatus,
        searchQuery, selectedMonth, selectedYear,
    ]);

    const getOrdersForDay = (
        asset: AssetAvailability, day: number,
    ): OrderForAssetAvailability[] => {
        const currentDay = dayLabel(day);

        return asset.orders.filter((order) => {
            const start = new Date(order.startDate);
            const end = new Date(order.endDate);
            // A malformed date colours nothing rather than taking the chart down
            // (`stationCalendarDay` throws on an invalid date).
            if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;
            const startDay = stationCalendarDay(start);
            // An end at midnight belongs to the day before it.
            const endDay = stationCalendarDay(new Date(end.getTime() - 1));
            return startDay <= currentDay && currentDay <= endDay;
        });
    };

    const handleCellClick = (asset: AssetAvailability, day: number) => {
        const cellOrders = getOrdersForDay(asset, day);
        if (cellOrders.length > 0) {
            setSearchParams({
                assetId: asset.assetId, day: day.toString(),
            });
        }
    };

    // The whole day across every object currently in the table. Same click
    // target as a cell, one level up: the column header stands for the day, so
    // clicking it asks about the day rather than about one object.
    const handleDayHeaderClick = (day: number) => {
        setSearchParams({ day: day.toString() });
    };

    const closeDialog = () => {
        setSearchParams({});
    };

    const selectedCell = useMemo(() => {
        const assetId = searchParams.get('assetId');
        const dayStr = searchParams.get('day');
        if (!assetId || !dayStr || !assetsData) return null;
        const day = parseInt(dayStr, 10);
        const asset = assetsData.find((a) => a.assetId === assetId);
        if (!asset || isNaN(day)) return null;
        const orders = getOrdersForDay(asset, day);
        if (orders.length === 0) return null;
        return { assetName: asset.assetName, day, orders };
    }, [searchParams, assetsData, selectedYear, selectedMonth]);

    // `day` without `assetId` is the all-objects overlay; with one it is the
    // single-object dialog above. The two can never both be open.
    const selectedDay = useMemo(() => {
        const dayStr = searchParams.get('day');
        if (!dayStr || searchParams.get('assetId')) return null;
        const day = parseInt(dayStr, 10);
        if (isNaN(day) || day < 1 || day > daysInMonth) return null;
        return day;
    }, [searchParams, daysInMonth]);

    // Which objects appear, and in what order, follows the table — the overlay
    // shows what the operator is currently looking at.
    //
    // Their bookings, however, come from the *unfiltered* asset, exactly as the
    // single-object dialog reads them. The status filter narrows `orders` on
    // the copies in `filteredData`, and a day view built on that set would not
    // just list fewer bookings than the dialog does for the same object — it
    // would derive its free windows from the survivors and report a fully
    // booked object as free all day, which is the one thing this view must
    // never get wrong.
    const overlayObjects = useMemo((): DayOverlayObject[] => {
        if (selectedDay === null || !assetsData) return [];
        const lockerLabel = t('dashboard.assets_availability.table.locker');
        return sortedAssets.map((asset) => {
            const unfiltered = assetsData.find((a) => a.assetId === asset.assetId) ?? asset;
            const useCase = asset.useCases?.[0];
            const subtitle = [
                hasLockerServices ? asset.locationName : null,
                useCase ? t(`service_type_selector.${useCase}`, useCase) : null,
            ].filter(Boolean).join(' · ');
            return {
                assetId: asset.assetId,
                displayName: assetDisplayLabel(asset, lockerLabel),
                subtitle: subtitle || undefined,
                orders: getOrdersForDay(unfiltered, selectedDay),
            };
        });
    }, [selectedDay, sortedAssets, assetsData, hasLockerServices, selectedYear, selectedMonth, t]);

    const buildCellTooltip = (
        orders: OrderForAssetAvailability[],
    ): string => {
        if (orders.length === 0) return '';
        return orders.map((o) => {
            const statusLabel = t(
                `dashboard.assets_availability.status.${o.status}`,
            );
            const start = format(new Date(o.startDate), 'dd.MM.');
            const end = format(new Date(o.endDate), 'dd.MM.');
            return `${o.bookingNumber} · ${statusLabel} · ${start}–${end}`;
        }).join('\n');
    };

    if (isLoading) {
        return (
            <div className={
                'w-full p-4 flex justify-center items-center ' +
                'h-64 text-foreground/60'
            }
            >
                <CircularProgress
                    size={24}
                    sx={{ color: 'rgb(var(--color-primary))' }}
                />
                <span className={'ml-3'}>
                    {t('dashboard.assets_availability.loading')}
                </span>
            </div>
        );
    }

    if (error) {
        return (
            <div className={
                'w-full p-4 flex justify-center items-center ' +
                'h-64 text-red-500'
            }
            >
                {t('dashboard.assets_availability.error')}
            </div>
        );
    }

    const locale = i18n.language === 'de' ? de : enUS;
    const monthNames = Array.from(
        { length: 12 },
        (_, i) => format(new Date(selectedYear, i), 'MMMM', { locale }),
    );

    const handlePrevMonth = () => {
        if (selectedMonth === 0) {
            setSelectedMonth(11);
            setSelectedYear((prev) => prev - 1);
        } else {
            setSelectedMonth((prev) => prev - 1);
        }
    };

    const handleNextMonth = () => {
        if (selectedMonth === 11) {
            setSelectedMonth(0);
            setSelectedYear((prev) => prev + 1);
        } else {
            setSelectedMonth((prev) => prev + 1);
        }
    };

    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from(
        { length: 5 }, (_, i) => currentYear - 2 + i,
    );

    const filters: Array<{
        id: string;
        label: string;
        value: string | number;
        onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
        options: Array<{ label: string, value: string | number }>;
    }> = [
        {
            id: 'year-select',
            label: t('dashboard.assets_availability.filters.year'),
            value: selectedYear,
            onChange: (e) => setSelectedYear(parseInt(e.target.value)),
            options: yearOptions.map(
                (y) => ({ label: y.toString(), value: y }),
            ),
        },
        {
            id: 'status-select',
            label: t('dashboard.assets_availability.filters.status'),
            value: selectedStatus,
            onChange: (e) => setSelectedStatus(
                e.target.value as BookingStatusType | 'all',
            ),
            options: [
                {
                    label: t('dashboard.assets_availability.filters.all'),
                    value: 'all',
                },
                ...Object.keys(statusColors).map((s) => ({
                    label: t(
                        `dashboard.assets_availability.status.${s}`,
                    ),
                    value: s,
                })),
            ],
        },
    ];

    if (hasLockerServices) {
        filters.push({
            id: 'location-select',
            label: t('dashboard.assets_availability.filters.location'),
            value: selectedLocation,
            onChange: (e) => setSelectedLocation(e.target.value),
            options: [
                {
                    label: t('dashboard.assets_availability.filters.all'),
                    value: 'all',
                },
                ...locationOptions.map(
                    (loc) => ({ label: loc, value: loc }),
                ),
            ],
        });
    }

    const allUseCases = Array.from(
        new Set(
            assetsData?.flatMap((a) => a.useCases || []).filter(Boolean),
        ),
    ).filter((uc) => !allowedUseCases?.length || allowedUseCases.includes(uc));

    filters.push({
        id: 'usecase-select',
        label: t('dashboard.assets_availability.filters.use_case'),
        value: selectedUseCase,
        onChange: (e) => setSelectedUseCase(e.target.value),
        options: [
            {
                label: t('dashboard.assets_availability.filters.all'),
                value: 'all',
            },
            ...allUseCases.map((u) => ({
                label: t(
                    `service_type_selector.${u}`,
                    u,
                ),
                value: u,
            })),
        ],
    });

    const showLocation = hasLockerServices;
    const stickyColClass = 'sticky z-[5] bg-inherit';
    const stickyHeaderColClass = 'sticky z-[15] bg-gray-50';
    const col1Left = 'left-0';
    const col2Left = 'left-[160px]';
    const col3Left = showLocation ? 'left-[288px]' : '';

    // ── Row renderers ──────────────────────────────────────────────────────
    //
    // Three row kinds share the sticky-column geometry and the day grid, so
    // they are built from the same pieces rather than three parallel tables.
    // `indent` is the only structural difference between them.

    const labelCellClass = `font-medium px-3 py-2 text-foreground border-b border-foreground/5 ${stickyColClass} ${col1Left}`;
    const plainCellClass = 'px-3 py-2 text-foreground/80 border-b border-foreground/5';
    const serviceColClass = showLocation
        ? `${stickyColClass} ${col3Left}`
        : `${stickyColClass} ${col2Left}`;

    /**
     * The three sticky columns, so every row kind lines up. The label cell
     * leads with a fixed slot for the expand chevron, empty on rows that do
     * not expand: names stay flush whether or not a row has a chevron, and
     * only real children are indented (PO, 2026-10-08 — an indent reads as
     * "belongs to the row above").
     */
    const stickyCells = (
        label: React.ReactNode,
        location: React.ReactNode,
        service: React.ReactNode,
        indent: number,
        toggle?: React.ReactNode,
    ) => (
        <>
            <td className={labelCellClass} style={{ paddingLeft: 12 + indent * 20 }}>
                <span className={'flex items-start gap-1'}>
                    <span className={'w-5 shrink-0 flex justify-center'}>{toggle}</span>
                    <span className={'min-w-0'}>{label}</span>
                </span>
            </td>
            {showLocation && (
                <td className={`${plainCellClass} ${stickyColClass} ${col2Left}`}>
                    {location}
                </td>
            )}
            <td className={`${plainCellClass} ${serviceColClass}`}>{service}</td>
        </>
    );

    const serviceLabel = (asset: AssetAvailability) => {
        const categoryName = asset.productId ? serviceNames.get(asset.productId) : undefined;
        if (categoryName) return categoryName;
        return asset.useCases?.length
            ? t(`service_type_selector.${asset.useCases[0]}`, asset.useCases[0])
            : '-';
    };

    const dayColumnBg = (dm: typeof dayMeta[number]) => {
        if (dm.isToday) return 'bg-primary/10';
        if (dm.isHoliday) return 'bg-gray-200/60';
        return dm.isWeekend ? 'bg-gray-100/80' : '';
    };

    /**
     * The day cells of one object — status-coloured, clickable, as before.
     *
     * Read on a product row this is exactly right, and that is the point:
     * `getAssetAvailabilityForMonth` hangs every order of a group onto every
     * asset of that group, so `asset.orders` is really the *product's* orders.
     * The data was never wrong, it was drawn one level too low.
     */
    const renderDayCells = (asset: AssetAvailability) => dayMeta.map((dm) => {
        const cellOrders = getOrdersForDay(asset, dm.day);
        const statusCounts: Record<BookingStatusType, number> = {
            booked: 0, active: 0, finished: 0,
            canceled: 0, reserved: 0, incompleted: 0,
            blocked: 0, conflicted: 0, expired: 0, overtime: 0,
        };
        cellOrders.forEach((order) => {
            statusCounts[order.status]++;
        });

        const dominant = getDominantStatus(statusCounts);
        const cellColor = dominant ? statusColors[dominant] : '';
        const tooltipText = buildCellTooltip(cellOrders);

        const cell = (
            <td
                key={dm.day}
                className={
                    'relative cursor-pointer border-b border-foreground/5 '
                    + `min-w-[32px] ${dayColumnBg(dm)}`
                }
                onClick={() => handleCellClick(asset, dm.day)}
            >
                <div className={`h-8 ${cellColor} transition-all rounded-sm mx-px`} />
            </td>
        );

        if (tooltipText) {
            return (
                <Tooltip
                    key={dm.day}
                    title={<span style={{ whiteSpace: 'pre-line' }}>{tooltipText}</span>}
                >
                    {cell}
                </Tooltip>
            );
        }
        return cell;
    });

    /**
     * The day cells of a station: booked or not, and nothing more.
     *
     * A station's counts cannot be aggregated from this source — the fan-out
     * above would count one booking once per object — but "is anything booked
     * here" survives duplication. So the row carries a single flat tone rather
     * than a scale, which is also the only reading that stays true when a
     * station mixes products that are not interchangeable.
     */
    const renderStationDayCells = (station: StationRow) => dayMeta.map((dm) => {
        const dayStart = stationInstant(dayLabel(dm.day));
        const dayEnd = new Date(stationDayEnd(dayLabel(dm.day)).getTime() + 1);
        // The occupies rule stays in `availabilityStatus`, which is the one
        // place that decides it — a cancelled booking no longer takes the
        // object off the market, so it must not light the day up either.
        const busy = stationHasBookingOn(
            station, dayStart, dayEnd, (order) => occupiesAsset(order.status),
        );

        return (
            <td
                key={dm.day}
                className={`border-b border-foreground/5 min-w-[32px] ${dayColumnBg(dm)}`}
            >
                <div
                    className={
                        `h-8 rounded-sm mx-px ${busy ? 'bg-foreground/20' : ''}`
                    }
                    title={busy
                        ? t('dashboard.assets_availability.table.station_day_busy')
                        : t('dashboard.assets_availability.table.station_day_free')}
                />
            </td>
        );
    });

    /** Day cells without bookings: a pooled unit, or a house holding no rooms. */
    const emptyDayCells = () => dayMeta.map((dm) => (
        <td
            key={dm.day}
            className={`border-b border-foreground/5 min-w-[32px] ${dayColumnBg(dm)}`}
        >
            <div className={'h-8 rounded-sm mx-px'} />
        </td>
    ));

    /** "Haus: Name" under a room whose house holds only it, linked to the house. */
    const houseMarker = (houseId: string, houseName: string) => (
        <span className={'block text-xs text-foreground/50 font-normal'}>
            {t('dashboard.assets_availability.table.house_label')}{': '}
            <Link
                to={makeStationDetailsUrl(houseId)}
                className={'hover:text-primary hover:underline transition-colors'}
            >
                {houseName}
            </Link>
        </span>
    );

    const renderObjectRow = (
        asset: AssetAvailability,
        indent: number,
        showDays: boolean,
        house?: { id: string, name: string },
    ) => (
        <tr
            key={asset.assetId}
            className={'hover:bg-gray-50/50 transition bg-text-contrast'}
        >
            {stickyCells(
                <span className={'flex flex-col'}>
                    <Link
                        to={makeAssetDetailsUrl(asset.assetId)}
                        className={'hover:text-primary hover:underline transition-colors'}
                    >
                        {assetDisplayLabel(asset, t('dashboard.assets_availability.table.locker'))}
                    </Link>
                    {house && houseMarker(house.id, house.name)}
                </span>,
                asset.locationName,
                serviceLabel(asset),
                indent,
            )}
            {showDays
                ? renderDayCells(asset)
                // A unit inside a pool has no bookings of its own: the object
                // is assigned when usage starts, so a future booking belongs
                // to the product, not to this compartment. Drawing the
                // product's bookings here would claim this very unit is taken.
                : emptyDayCells()}
        </tr>
    );

    /** "Weg 7, 54321 Musterstadt" from whatever parts the house has. */
    const houseAddressLine = (house: HouseListItem) => {
        const address = house.address;
        if (!address) return '';
        const street = [address.street, address.houseNumber].filter(Boolean).join(' ');
        const town = [address.postalCode, address.city].filter(Boolean).join(' ');
        return [street, town].filter(Boolean).join(', ');
    };

    /**
     * A house without rooms: its own row, linked to the house, so a house that
     * was created stays findable instead of becoming a record nobody knows of.
     */
    const renderEmptyHouse = (house: HouseListItem) => (
        <tr key={`house:${house.id}`} className={'hover:bg-gray-50/50 transition bg-text-contrast'}>
            {stickyCells(
                <span className={'flex flex-col'}>
                    <Link
                        to={makeStationDetailsUrl(house.id)}
                        className={'text-left hover:text-primary hover:underline transition-colors'}
                    >
                        {house.name}
                    </Link>
                    <span className={'text-xs text-foreground/50 font-normal'}>
                        {t('dashboard.assets_availability.table.house_no_rooms')}
                    </span>
                </span>,
                houseAddressLine(house) || t('common.not_applicable'),
                t('dashboard.assets_availability.table.house_label'),
                0,
            )}
            {emptyDayCells()}
        </tr>
    );

    const renderProductRow = (product: ProductRow, station: StationRow) => {
        const key = `${station.stationId}::${product.productId}`;
        const isOpen = expandedProducts.has(key);
        const representative = product.objects[0] as AssetAvailability;

        return (
            <React.Fragment key={key}>
                <tr className={'hover:bg-gray-50/50 transition bg-text-contrast'}>
                    {stickyCells(
                        <button
                            type={'button'}
                            onClick={() => toggleKey(setExpandedProducts, key)}
                            className={'flex items-center gap-1 text-left hover:text-primary transition-colors'}
                            aria-expanded={isOpen}
                        >
                            <span>{product.productName}</span>
                            <span className={'text-foreground/50 font-normal'}>
                                {t('dashboard.assets_availability.table.units', {
                                    count: product.objects.length,
                                })}
                            </span>
                        </button>,
                        representative.locationName,
                        serviceLabel(representative),
                        1,
                        <button
                            type={'button'}
                            onClick={() => toggleKey(setExpandedProducts, key)}
                            className={'flex items-center hover:text-primary transition-colors'}
                            aria-hidden
                            tabIndex={-1}
                        >
                            {isOpen
                                ? <KeyboardArrowDownIcon fontSize={'small'} />
                                : <KeyboardArrowRightIcon fontSize={'small'} />}
                        </button>,
                    )}
                    {renderDayCells(representative)}
                </tr>
                {isOpen && product.objects.map(
                    (object) => renderObjectRow(object, 2, false),
                )}
            </React.Fragment>
        );
    };

    /**
     * The service of a station row: one label when every object beneath it
     * has the same one, "Mehrere" only when they really differ. Counted by
     * service, not by product — a house's rooms are each their own product but
     * all "Veranstaltungsraum" (PO, 2026-10-08).
     */
    const stationServiceLabel = (station: StationRow) => {
        const labels = station.products.flatMap(
            (product) => product.objects.map((object) => serviceLabel(object)),
        );
        return labels.every((label) => label === labels[0])
            ? labels[0]
            : t('dashboard.assets_availability.table.mixed_services');
    };

    const renderStation = (station: StationRow) => {
        // A station holding exactly one object is that object — a venue or
        // apartment, or a single rental product. One row, no chevron, exactly
        // as before (architecture §2d).
        if (station.objectCount === 1) {
            const only = station.products[0]?.objects[0];
            // A real house holding one room stays one row too, but says so:
            // the house is otherwise nowhere to be seen (houseRows.ts).
            const houseName = houseNames.get(station.stationId);
            return only
                ? renderObjectRow(only, 0, true, houseName ? { id: station.stationId, name: houseName } : undefined)
                : null;
        }

        // Flipped against the default: closed, or open for a tenant's only house.
        const isOpen = expandedStations.has(station.stationId)
            !== (station.stationId === autoOpenStationId);

        return (
            <React.Fragment key={station.stationId}>
                <tr className={'hover:bg-gray-50/50 transition bg-text-contrast'}>
                    {stickyCells(
                        // The chevron expands, the NAME opens the station.
                        // Two actions, so two controls: one button that both
                        // expanded and navigated would make every attempt to
                        // look inside a station leave the page. The object
                        // count is gone from the name (PO, 2026-10-08): the
                        // rows beneath say it, and beside a long name it
                        // wrapped into the column.
                        <Link
                            to={makeStationDetailsUrl(station.stationId)}
                            className={'text-left hover:text-primary hover:underline transition-colors'}
                        >
                            {station.stationName}
                        </Link>,
                        // The address, and who else stands at it. A shared
                        // location is not a detail: editing the address from
                        // one station changes it for every station here, and
                        // this is where an operator first learns that (PO,
                        // 2026-09-10).
                        <span className={'flex flex-col'}>
                            <span>{station.locationName}</span>
                            {station.siblingStationNames.length > 0 && (
                                <span className={'text-foreground/50'}>
                                    {t('dashboard.assets_availability.table.shared_location', {
                                        count: station.siblingStationNames.length + 1,
                                    })}
                                </span>
                            )}
                        </span>,
                        stationServiceLabel(station),
                        0,
                        <button
                            type={'button'}
                            onClick={() => toggleKey(setExpandedStations, station.stationId)}
                            className={'flex items-center hover:text-primary transition-colors'}
                            aria-expanded={isOpen}
                            aria-label={t(isOpen
                                ? 'dashboard.assets_availability.table.collapse_station'
                                : 'dashboard.assets_availability.table.expand_station')}
                        >
                            {isOpen
                                ? <KeyboardArrowDownIcon fontSize={'small'} />
                                : <KeyboardArrowRightIcon fontSize={'small'} />}
                        </button>,
                    )}
                    {renderStationDayCells(station)}
                </tr>
                {isOpen && station.products.map((product) => (product.expandable
                    ? renderProductRow(product, station)
                    : renderObjectRow(product.objects[0] as AssetAvailability, 1, true)))}
            </React.Fragment>
        );
    };

    return (
        <div className={'w-full min-w-0'}>
            {/* Loading overlay */}
            {isFetching && !isLoading && (
                <div className={'flex justify-center items-center py-2'}>
                    <CircularProgress
                        size={20}
                        sx={{ color: 'rgb(var(--color-primary))' }}
                    />
                </div>
            )}

            {/* Filters.

                `justify-start` until `sm`: with `justify-end` every wrapped
                line right-aligns on its own, so on a phone the four controls
                came out at four different indents (QA #2439, defect G). Each
                control is also full width there — stacked and aligned reads as
                a form; four ragged part-width boxes read as a bug. */}
            <div className={
                'flex flex-wrap justify-start sm:justify-end items-end gap-4 mb-4'
            }
            >
                {/* Month with arrows */}
                <div className={'flex w-full sm:w-auto min-w-0 flex-col'}>
                    <label className={
                        'text-sm font-medium text-foreground/60 mb-1'
                    }
                    >
                        {t('dashboard.assets_availability.filters.month')}
                    </label>
                    <div className={
                        'flex items-center gap-2 rounded-[5px] ' +
                        'px-3 py-1.5 bg-text-contrast shadow-sm'
                    }
                    >
                        <button
                            onClick={handlePrevMonth}
                            className={
                                'text-foreground/60 ' +
                                'hover:text-foreground transition'
                            }
                            aria-label={t(
                                'dashboard.assets_availability' +
                                '.filters.previous_month',
                            )}
                        >
                            <ChevronLeftIcon fontSize={'small'} />
                        </button>
                        <span className={
                            'text-foreground ' +
                            'w-20 text-center text-sm'
                        }
                        >
                            {monthNames[selectedMonth]}
                        </span>
                        <button
                            onClick={handleNextMonth}
                            className={
                                'text-foreground/60 ' +
                                'hover:text-foreground transition'
                            }
                            aria-label={t(
                                'dashboard.assets_availability' +
                                '.filters.next_month',
                            )}
                        >
                            <ChevronRightIcon fontSize={'small'} />
                        </button>
                    </div>
                </div>

                {/* Other filters */}
                {filters.map(({ id, label: lbl, value, onChange, options }) => (
                    <div key={id} className={'flex w-full sm:w-auto min-w-0 flex-col'}>
                        <label
                            htmlFor={id}
                            className={
                                'text-sm font-medium ' +
                                'text-foreground/60 mb-1'
                            }
                        >
                            {lbl}
                        </label>
                        <select
                            id={id}
                            value={value}
                            onChange={onChange}
                            className={
                                'w-full sm:w-auto max-w-full ' +
                                'rounded-[5px] px-3 py-1.5 ' +
                                'bg-text-contrast shadow-sm ' +
                                'text-foreground text-sm ' +
                                'hover:border-foreground/40 ' +
                                'focus:border-primary ' +
                                'focus:outline-none transition-all'
                            }
                        >
                            {options?.map((opt) => (
                                <option
                                    key={opt.value}
                                    value={opt.value}
                                >
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </div>
                ))}
            </div>

            {/* Legend */}
            <div className={
                'flex flex-wrap gap-x-4 gap-y-1 mb-4 ' +
                'text-sm justify-start sm:justify-end'
            }
            >
                {Object.entries(statusColors).map(([status, color]) => (
                    <div
                        key={status}
                        className={'flex items-center gap-1.5'}
                    >
                        <div className={
                            `w-3 h-3 rounded-sm ${color}`
                        }
                        ></div>
                        <span className={'text-foreground/60'}>
                            {t(`dashboard.assets_availability.status.${status}`)}
                        </span>
                    </div>
                ))}
            </div>

            {/* Table — always rendered (even when empty) so the
                Objektübersicht has a stable structural skeleton. Empty
                state is a single tbody row spanning all columns rather
                than replacing the table with a centered message — keeps
                the user's mental model of "this is where my assets
                live" before any objects exist. */}
            <div className={
                'w-full overflow-x-auto overflow-y-auto ' +
                'max-h-[600px] rounded-[5px] shadow-sm ' +
                'bg-text-contrast'
            }
            >
                <div className={'min-w-max'}>
                    <table className={
                        'border-collapse text-xs ' +
                        'min-w-full table-auto'
                    }
                    >
                        <thead>
                            <tr className={
                                'sticky top-[-1px] z-10 bg-gray-50'
                            }
                            >
                                {/* Asset name header – sticky */}
                                <th className={
                                    'text-left px-3 py-2 w-40 ' +
                                    'font-semibold text-foreground/60 ' +
                                    'border-b border-foreground/10 ' +
                                    `${stickyHeaderColClass} ${col1Left}`
                                }
                                >
                                    {/* The same chevron slot as the rows, so the
                                        heading stands over the names. */}
                                    <span className={'flex gap-1'}>
                                        <span className={'w-5 shrink-0'} />
                                        <span>{t('dashboard.assets_availability.table.asset')}</span>
                                    </span>
                                </th>
                                {/* Location header – sticky */}
                                {showLocation && (
                                    <th className={
                                        'text-left px-3 py-2 w-32 ' +
                                        'font-semibold text-foreground/60 ' +
                                        'border-b border-foreground/10 ' +
                                        `${stickyHeaderColClass} ${col2Left}`
                                    }
                                    >
                                        {t('dashboard.assets_availability.table.location')}
                                    </th>
                                )}
                                {/* Service header – sticky */}
                                <th className={
                                    'text-left px-3 py-2 w-28 ' +
                                    'font-semibold text-foreground/60 ' +
                                    'border-b border-foreground/10 ' +
                                    (showLocation
                                        ? `${stickyHeaderColClass} ${col3Left}`
                                        : `${stickyHeaderColClass} ${col2Left}`)
                                }
                                >
                                    {t('dashboard.assets_availability.table.service')}
                                </th>
                                {/* Day headers */}
                                {dayMeta.map((dm) => {
                                    const headerBg = dm.isToday
                                        ? 'bg-primary/10'
                                        : dm.isHoliday
                                            ? 'bg-gray-300/60'
                                            : dm.isWeekend
                                                ? 'bg-gray-200/60'
                                                : '';
                                    const topBorder = dm.isToday
                                        ? 'border-t-2 border-t-primary'
                                        : '';
                                    const thContent = (
                                        <th
                                            key={dm.day}
                                            className={
                                                'text-center px-0.5 pt-1 pb-0.5 ' +
                                                'font-semibold min-w-[32px] w-[32px] ' +
                                                'text-foreground/60 ' +
                                                'border-b border-foreground/10 ' +
                                                `${headerBg} ${topBorder}`
                                            }
                                        >
                                            {/* The date opens the whole day across every
                                                object; a cell stays the single-object view. */}
                                            <button
                                                type={'button'}
                                                onClick={() => handleDayHeaderClick(dm.day)}
                                                aria-label={t(
                                                    'dashboard.assets_availability.day_view.open',
                                                    { day: dm.day },
                                                )}
                                                className={
                                                    'w-full hover:text-primary ' +
                                                    'transition-colors cursor-pointer'
                                                }
                                            >
                                                <div className={'leading-tight underline decoration-dotted underline-offset-2'}>
                                                    {dm.day}
                                                </div>
                                                <div className={
                                                    'text-[9px] font-normal ' +
                                                    'text-foreground/40'
                                                }
                                                >
                                                    {dm.weekdayShort}
                                                </div>
                                            </button>
                                        </th>
                                    );
                                    if (dm.isHoliday) {
                                        return (
                                            <Tooltip
                                                key={dm.day}
                                                title={dm.holidayName}
                                            >
                                                {thContent}
                                            </Tooltip>
                                        );
                                    }
                                    return thContent;
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {/* Only show "no assets" once a fetch settles — not
                                during a post-mutation refetch (e.g. right after a
                                delete), when `filteredData` is transiently empty
                                while new data loads. `isFetching` covers both the
                                initial load and refetches. */}
                            {filteredData.length === 0 && emptyHouses.length === 0 && !isFetching && (
                                <tr>
                                    <td
                                        colSpan={dayMeta.length + (showLocation ? 3 : 2)}
                                        className={
                                            'text-center py-16 ' +
                                            'text-foreground/60 text-sm'
                                        }
                                    >
                                        {t('dashboard.assets_availability.empty')}
                                    </td>
                                </tr>
                            )}
                            {stationRows.map((station) => renderStation(station))}
                            {emptyHouses.map((house) => renderEmptyHouse(house))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Detail Dialog */}
            <StyledDialog
                open={!!selectedCell}
                onClose={() => closeDialog()}
                maxWidth={'xl'}
                fullWidth
                slotProps={{
                    backdrop: { className: classes.backdropOpacity },
                }}
            >
                {selectedCell && (
                    <>
                        <DialogTitle className={classes.dialogTitle}>
                            {selectedCell.assetName} –{' '}
                            {format(
                                new Date(
                                    selectedYear,
                                    selectedMonth,
                                    selectedCell.day,
                                ),
                                'PPP',
                                { locale },
                            )}
                        </DialogTitle>

                        <DialogContent
                            className={classes.dialogContent}
                            dividers
                        >
                            {/* Same component the all-objects overlay stacks, so
                                one object's day reads identically in both. */}
                            <table className={'w-full text-sm table-fixed'}>
                                <DayTimelineHead />
                                <AssetDayOrders
                                    day={new Date(
                                        selectedYear,
                                        selectedMonth,
                                        selectedCell.day,
                                    )}
                                    orders={selectedCell.orders}
                                />
                            </table>
                        </DialogContent>

                        <DialogActions>
                            <Button
                                className={classes.closeButton}
                                onClick={() => closeDialog()}
                                variant={'contained'}
                                sx={{
                                    backgroundColor: 'rgb(var(--color-primary))',
                                }}
                            >
                                {t('dashboard.assets_availability.dialog.close')}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </StyledDialog>

            {/* Day overlay — every object at once, opened from a column header */}
            {selectedDay !== null && (
                <DayOccupancyOverlay
                    day={new Date(selectedYear, selectedMonth, selectedDay)}
                    locale={locale}
                    objects={overlayObjects}
                    onClose={closeDialog}
                />
            )}
        </div>
    );
};
